import { Fragment } from "react";
import { Check, MoreHorizontal } from "lucide-react";
import { PermissionGuard } from "components/common/PermissionGuard";
import { Button } from "components/common/buttons/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "components/common/dropdown/DropDownV2";
import { cn } from "utils/cn";
import type { MoreActionsMenuItem, MoreActionsMenuProps } from "./MoreActionsMenu.types";

const renderItem = (item: MoreActionsMenuItem) => {
  const row = item.children?.length ? (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger disabled={item.disabled} title={item.title} className="gap-2">
        {item.icon}
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className={cn("min-w-[12rem]", item.submenuContentClassName)}>
        {item.children.map((child) => (
          <Fragment key={child.id}>{renderItem(child)}</Fragment>
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  ) : (
    <DropdownMenuItem
      variant={item.variant}
      disabled={item.disabled}
      title={item.title}
      aria-current={item.selected ? "true" : undefined}
      onSelect={() => item.onSelect?.()}
    >
      {item.icon}
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {item.selected ? (
        <Check className="ml-auto size-4 shrink-0 text-primary" aria-hidden />
      ) : null}
    </DropdownMenuItem>
  );

  if (item.permission) {
    return (
      <PermissionGuard permission={item.permission} mode="hide">
        {row}
      </PermissionGuard>
    );
  }
  return row;
};

/**
 * Shared "⋯ More actions" overflow menu (Jira-style) built on DropDownV2.
 * Consolidates secondary actions behind a single trigger, with optional grouped
 * sections and per-group / per-item permission gating. Keep primary actions as
 * standalone buttons; only fold secondary/file actions in here.
 */
export const MoreActionsMenu = ({
  groups,
  label = "More actions",
  disabledTitle,
  title,
  triggerIcon,
  triggerClassName,
  permission,
  align = "end",
  contentClassName,
  disabled = false,
  modal = true,
  open,
  onOpenChange,
}: MoreActionsMenuProps) => {
  const trigger = (
    <DropdownMenuTrigger asChild>
      <Button
        variant="ghostSecondary"
        size="sm"
        disabled={disabled}
        aria-label={label}
        title={disabled && disabledTitle ? disabledTitle : (title ?? label)}
        className={triggerClassName}
      >
        {triggerIcon ?? <MoreHorizontal />}
      </Button>
    </DropdownMenuTrigger>
  );

  return (
    <DropdownMenu modal={modal} open={open} onOpenChange={onOpenChange}>
      {permission ? (
        <PermissionGuard permission={permission} mode="hide">
          {trigger}
        </PermissionGuard>
      ) : (
        trigger
      )}
      <DropdownMenuContent align={align} className={cn("min-w-[12rem]", contentClassName)}>
        {groups.map((group, index) => {
          const body = (
            <>
              {index > 0 ? <DropdownMenuSeparator /> : null}
              {group.label ? (
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  {group.label}
                </DropdownMenuLabel>
              ) : null}
              {group.items.map((item) => (
                <Fragment key={item.id}>{renderItem(item)}</Fragment>
              ))}
            </>
          );

          if (group.permission) {
            return (
              <PermissionGuard key={group.id} permission={group.permission} mode="hide">
                {body}
              </PermissionGuard>
            );
          }
          return <Fragment key={group.id}>{body}</Fragment>;
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
